import { useEffect, useState } from "react";
import api, { formatApiError } from "@/api";
import { useLang } from "@/i18n";
import Layout from "@/components/Layout";
import { toast } from "sonner";
import { GraduationCap, Trash2, Plus, Pencil, X, Headphones } from "lucide-react";

const CATS = ["tecnica", "recupero", "lifestyle"];
const MED_THEMES = ["rilassamento", "sonno", "respirazione", "preworkout"];

export default function CoachEducation() {
  const { t } = useLang();
  const [items, setItems] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("tecnica");
  const [body, setBody] = useState("");
  const [saving, setSaving] = useState(false);
  const [medItems, setMedItems] = useState([]);
  const [medTitle, setMedTitle] = useState("");
  const [medTheme, setMedTheme] = useState("rilassamento");
  const [medUrl, setMedUrl] = useState("");
  const [medDuration, setMedDuration] = useState(10);
  const [medSaving, setMedSaving] = useState(false);

  const nav = [
    { to: "/coach", key: "dashboard", label: t("nav.dashboard"), end: true },
    { to: "/coach/clients", key: "clients", label: t("nav.clients") },
    { to: "/coach/exercises", key: "exercises", label: t("nav.exercises") },
    { to: "/coach/programs", key: "programs", label: t("nav.programs") },
    { to: "/coach/nutrition", key: "nutrition", label: t("nav.nutrition") },
    { to: "/coach/supplements", key: "supplements", label: t("nav.supplements") },
    { to: "/coach/stretching", key: "stretching", label: t("nav.stretching") },
    { to: "/coach/shop", key: "shop", label: t("nav.shop") },
    { to: "/coach/testimonials", key: "testimonials", label: t("nav.testimonials") },
    { to: "/coach/education", key: "education", label: t("nav.education") },
  ];

  const load = () => {
    api.get("/coach/education").then((r) => setItems(r.data)).catch(() => {});
    api.get("/coach/meditations").then((r) => setMedItems(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const reset = () => { setEditingId(null); setTitle(""); setCategory("tecnica"); setBody(""); };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingId) {
        await api.put(`/coach/education/${editingId}`, { title, category, body });
        toast.success(t("edu.updated"));
      } else {
        await api.post("/coach/education", { title, category, body });
        toast.success(t("edu.saved"));
      }
      reset();
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const edit = (a) => { setEditingId(a._id); setTitle(a.title); setCategory(a.category); setBody(a.body); };

  const remove = async (id) => {
    try {
      await api.delete(`/coach/education/${id}`);
      toast.success(t("edu.deleted"));
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const medSave = async (e) => {
    e.preventDefault();
    setMedSaving(true);
    try {
      await api.post("/coach/meditations", { title: medTitle, theme: medTheme, url: medUrl, duration_min: medDuration });
      toast.success(t("med.saved"));
      setMedTitle("");
      setMedUrl("");
      setMedTheme("rilassamento");
      setMedDuration(10);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setMedSaving(false);
    }
  };

  const medRemove = async (id) => {
    try {
      await api.delete(`/coach/meditations/${id}`);
      toast.success(t("med.deleted"));
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <Layout nav={nav}>
      <div className="max-w-3xl space-y-8" data-testid="coach-education-page">
        <h1 className="text-3xl font-black uppercase tracking-tight flex items-center gap-2">
          <GraduationCap className="w-7 h-7 text-primary" /> {t("edu.title")}
        </h1>
        <p className="text-sm text-muted-foreground -mt-4">{t("edu.hint")}</p>

        <form onSubmit={save} className="kc-card p-5 space-y-4" data-testid="education-form">
          <h2 className="font-bold">{editingId ? t("edu.editing") : t("edu.add")}</h2>
          <div className="grid sm:grid-cols-3 gap-4">
            <div className="sm:col-span-2">
              <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{t("edu.articleTitle")}</label>
              <input data-testid="education-title-input" required maxLength={120} value={title}
                onChange={(e) => setTitle(e.target.value)} className="kc-input mt-1" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{t("edu.category")}</label>
              <select data-testid="education-category-select" value={category}
                onChange={(e) => setCategory(e.target.value)} className="kc-input mt-1">
                {CATS.map((c) => <option key={c} value={c}>{t(`edu.${c}`)}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{t("edu.body")}</label>
            <textarea data-testid="education-body-input" required rows={8} value={body}
              onChange={(e) => setBody(e.target.value)} className="kc-input mt-1" />
          </div>
          <div className="flex gap-2">
            <button data-testid="education-submit-button" type="submit" disabled={saving || !title.trim() || !body.trim()}
              className="kc-btn-primary inline-flex items-center gap-2">
              <Plus className="w-4 h-4" /> {saving ? t("common.loading") : editingId ? t("edu.update") : t("edu.add")}
            </button>
            {editingId && (
              <button type="button" data-testid="education-cancel-edit" onClick={reset} className="kc-btn-ghost inline-flex items-center gap-2">
                <X className="w-4 h-4" /> {t("common.cancel")}
              </button>
            )}
          </div>
        </form>

        <div className="space-y-3" data-testid="education-list">
          {items.length === 0 && <p className="text-muted-foreground text-sm">{t("edu.empty")}</p>}
          {items.map((a) => (
            <div key={a._id} className="kc-card p-4" data-testid={`coach-education-${a._id}`}>
              <div className="flex items-start gap-3">
                <div className="flex-1 min-w-0">
                  <span className="kc-badge bg-primary/12 text-primary mr-2">{t(`edu.${a.category}`)}</span>
                  <span className="font-bold">{a.title}</span>
                  <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{a.body}</p>
                </div>
                <button data-testid={`education-edit-${a._id}`} onClick={() => edit(a)} className="kc-btn-ghost !px-2.5 shrink-0">
                  <Pencil className="w-4 h-4" />
                </button>
                <button data-testid={`education-delete-${a._id}`} onClick={() => remove(a._id)} className="kc-btn-ghost !px-2.5 shrink-0">
                  <Trash2 className="w-4 h-4 text-destructive" />
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="pt-6 border-t border-border" data-testid="coach-meditations-section">
          <h2 className="text-xl font-black uppercase tracking-tight flex items-center gap-2 mb-1">
            <Headphones className="w-6 h-6 text-primary" /> {t("med.section")}
          </h2>
          <p className="text-sm text-muted-foreground mb-4">{t("med.hintCoach")}</p>
          <form onSubmit={medSave} className="kc-card p-5 space-y-4" data-testid="meditation-form">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{t("edu.articleTitle")}</label>
                <input data-testid="meditation-title-input" required maxLength={120} value={medTitle}
                  onChange={(e) => setMedTitle(e.target.value)} className="kc-input mt-1" />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{t("edu.category")}</label>
                <select data-testid="meditation-theme-select" value={medTheme}
                  onChange={(e) => setMedTheme(e.target.value)} className="kc-input mt-1">
                  {MED_THEMES.map((th) => <option key={th} value={th}>{t(`med.${th}`)}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{t("med.urlLabel")}</label>
                <input data-testid="meditation-url-input" required value={medUrl}
                  onChange={(e) => setMedUrl(e.target.value)} className="kc-input mt-1"
                  placeholder="https://youtube.com/... · open.spotify.com/... · file .mp3" />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{t("med.duration")}</label>
                <input data-testid="meditation-duration-input" type="number" min="1" max="180" value={medDuration}
                  onChange={(e) => setMedDuration(Number(e.target.value))} className="kc-input mt-1" />
              </div>
            </div>
            <button data-testid="meditation-submit-button" type="submit" disabled={medSaving || !medTitle.trim() || !medUrl.trim()}
              className="kc-btn-primary inline-flex items-center gap-2">
              <Plus className="w-4 h-4" /> {medSaving ? t("common.loading") : t("med.add")}
            </button>
          </form>
          <div className="space-y-3 mt-4" data-testid="meditation-list">
            {medItems.length === 0 && <p className="text-muted-foreground text-sm">{t("med.emptyCoach")}</p>}
            {medItems.map((m) => (
              <div key={m._id} className="kc-card p-4" data-testid={`coach-meditation-${m._id}`}>
                <div className="flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <span className="kc-badge bg-primary/12 text-primary mr-2">{t(`med.${m.theme}`)}</span>
                    <span className="font-bold">{m.title}</span>
                    <span className="text-xs text-muted-foreground ml-2">{m.duration_min} {t("med.minutes")}</span>
                    <p className="text-sm text-muted-foreground mt-1 truncate">{m.url}</p>
                  </div>
                  <button data-testid={`meditation-delete-${m._id}`} onClick={() => medRemove(m._id)} className="kc-btn-ghost !px-2.5 shrink-0">
                    <Trash2 className="w-4 h-4 text-destructive" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Layout>
  );
}
